import { Flex } from '../UI/Flex/Flex';
import * as style from './ChatMessageAgent.module.less';

import { ChatMessageData } from '@assets/model/RagAPI';
import { useStore } from '@assets/model/Store';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { observer } from 'mobx-react';
import { Button } from 'react-bootstrap';
import { LogoBird } from '../Header/Logo';
import Checkbox from '../UI/Checkbox/Checkbox';
import { AgentSectionScore } from './AgentSectionScore/AgentSectionScore';
import { observable } from 'mobx';
import { useState } from 'react';

interface ChatMessageProps {
    msg: ChatMessageData;
    response?: boolean;
    improveSections: AgentSectionRecommendation[];
    onUseRecommendation: (recommendation: string) => void;
    onFinish: () => void;
    isDisabled?: boolean;
}

export interface AgentSectionRecommendation {
    section_name: string;
    section_rating: number;
    section_improvements: string[];
}

class SelectedImprovementsStore {
    @observable accessor selectedImprovements: {
        [sectionName: string]: boolean[];
    } = {};

    isSelected(sectionName: string, index: number): boolean {
        if (!this.selectedImprovements[sectionName]) {
            this.selectedImprovements[sectionName] = new Array(30).fill(false);
        }

        return this.selectedImprovements[sectionName][index];
    }

    toggleImprovement(sectionName: string, index: number) {
        if (!this.selectedImprovements[sectionName]) {
            this.selectedImprovements[sectionName] = new Array(30).fill(false);
        }
        if (this.selectedImprovements[sectionName].hasOwnProperty(index)) {
            this.selectedImprovements[sectionName][index] =
                !this.selectedImprovements[sectionName][index];
        }
    }
}
export const ChatMessageAgent = observer((p: ChatMessageProps) => {
    const [isSelected] = useState(new SelectedImprovementsStore());
    const msg: ChatMessageData = p.msg;
    const clssses = classNames(style.chatmessage, 'fade-in');
    const companyId = useStore().selectedReportId;

    const convertMarkdown = (markdown: string): string => {
        if (!markdown) {
            return 'ERROR: No message content';
        }
        const rawHtmlWithDefault = marked.parse(markdown, { async: false });
        return DOMPurify.sanitize(rawHtmlWithDefault as string);
    };

    //const formatterHTML = convertMarkdown(msg.message);
    const createSection = (
        section: AgentSectionRecommendation
    ): JSX.Element => {
        return (
            <Flex
                key={section.section_name}
                style={{ width: '100%' }}
                wrap="wrap"
                className={style.section_container}
            >
                <Flex
                    style={{ width: '100%' }}
                    className={style.section_header}
                >
                    <div className={style.section_name}>
                        {section.section_name}
                    </div>
                    <div style={{ flexGrow: 1 }}></div>
                    <div className={style.section_rating}>
                        <AgentSectionScore score={section.section_rating} />
                    </div>
                </Flex>

                <Flex style={{ width: '100%' }} direction="column">
                    {section.section_improvements.map((improvement, index) => (
                        <Flex
                            key={index}
                            style={{ width: '100%' }}
                            alignItems="start"
                        >
                            <div
                                className={style.improvement_checkbox_container}
                            >
                                <Checkbox
                                    onChange={(value: boolean) => {
                                        if (p.isDisabled) return;

                                        isSelected.toggleImprovement(
                                            section.section_name,
                                            index
                                        );
                                    }}
                                    checked={isSelected.isSelected(
                                        section.section_name,
                                        index
                                    )}
                                />
                            </div>

                            <div className={style.section_improvement_entry}>
                                {improvement}
                            </div>
                        </Flex>
                    ))}
                </Flex>
            </Flex>
        );
    };
    const sectionsHtml = (p.improveSections ?? []).map(section =>
        createSection(section)
    );

    const onUseRecommendationCallback = () => {
        let recommendationText = '';
        (p.improveSections ?? []).forEach(section => {
            let didAddSectionHeader = false;

            section.section_improvements.forEach((improvement, index) => {
                if (isSelected.isSelected(section.section_name, index)) {
                    if (!didAddSectionHeader) {
                        recommendationText += `Section: ${section.section_name}\n`;
                        didAddSectionHeader = true;
                    }

                    recommendationText += `- ${improvement}\n`;
                }
            });
            recommendationText += '\n';
        });
        p.onUseRecommendation(recommendationText);
    };

    return (
        <>
            <Flex className={clssses} direction="row" wrap="wrap">
                <div
                    style={{
                        marginRight: '16px',
                        flexShrink: 0,
                        alignSelf: 'flex-start'
                    }}
                >
                    <LogoBird />
                </div>
                <div
                    className={`${style.aiContent} ${style.agentMessage}`}
                    style={{ marginRight: '48px' }}
                >
                    <Flex style={{ width: '100%' }} wrap="wrap">
                        {sectionsHtml}
                    </Flex>
                    {/* <div dangerouslySetInnerHTML={{ __html: formatterHTML }} /> */}
                    <Flex
                        style={{ width: '100%' }}
                        justify="center"
                        direction="row"
                    >
                        <Button
                            style={{
                                filter: 'saturate(0.85)'
                            }}
                            variant="outline-success"
                            className="me-3"
                            disabled={p.isDisabled}
                            onClick={() => {
                                onUseRecommendationCallback();
                            }}
                        >
                            Improve
                        </Button>
                        <Button
                            style={{
                                filter: 'saturate(0.85)'
                            }}
                            variant="success"
                            className="me-3"
                            disabled={p.isDisabled}
                            onClick={() => {
                                p.onFinish();
                            }}
                        >
                            Finish
                        </Button>
                    </Flex>
                </div>
            </Flex>
            {/* {files_references} */}
        </>
    );
});
